import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AuthHeaderService } from 'app/shared/services/authheaderservice';
import { environment } from 'environments/environment';
import { Observable } from 'rxjs';
import { retry } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})

export class DashboardService {
  private readonly constantsHeaders: HttpHeaders;

    constructor(
      private http: HttpClient,
      private authHeaderService: AuthHeaderService,
    ) { }

    fetchWalletBalance(identifier: any): Observable<any> {
      return this.http.get(
          `${environment.BUSINESS_API_URL}/source/wallet/balance/${identifier}`
      );
    }

    getAccountStatement (from: string, to: string, account_id: string, iso: string, download: boolean = false): Observable<any> {
      const xAuthHeader = this.authHeaderService.generateAuthHeader({});
      const headers = new HttpHeaders()
      .set('X-Auth-Signature', xAuthHeader)
      .set('X-APP-ID', `${environment.X_APP_ID}`)
      .set('X-ISSUER-ID', `${environment.X_ISSUER_ID}`);
      
      let url = `${environment.API_URL}/xas/v1/transactions/statements/${iso}/${account_id}?from_date=${from}&to_date=${to}&download=${download}`;

      return this.http.get(url, {headers});
    }

    getTransactionHistory (page_num: any, limit: any, merchant_id: string, product_id: string): Observable<any> {
      const xAuthHeader = this.authHeaderService.generateAuthHeader({});
      const headers = new HttpHeaders()
      .set('X-Auth-Signature', xAuthHeader)
      .set('X-APP-ID', `${environment.X_APP_ID}`)
      .set('X-ISSUER-ID', `${environment.X_ISSUER_ID}`);
      
      let url = `${environment.API_URL}/transactions-service/merchant/transactions/${merchant_id}?page_num=${page_num}&limit=${limit}&product_id=${product_id}`;

      return this.http.get(url, {headers});
    }

    fetchMerchantProducts = (merchant_id: string): Observable<any> => {
      return this.http.get(`${environment.API_URL}/business-api/products/merchant/${merchant_id}`);
    }

    createCategory(payload: any): Observable<any> {
      return this.http.post(`${environment.API_URL}/xmkt/v1/categories`, payload).pipe(retry(3))
    }

    updateCategory(payload: any): Observable<any> {
      return this.http.put(`${environment.API_URL}/xmkt/v1/categories`, payload).pipe(retry(3))
    }

    deleteCategory(id: any): Observable<any> {
      return this.http.delete(`${environment.API_URL}/xmkt/v1/categories/${id}`).pipe(retry(3))
    }

    fetchStores(): Observable<any> {
      return this.http.get(`${environment.API_URL}/xmkt/v1/stores`).pipe(retry(3))
    }

    fetchCategories(): Observable<any> {
      return this.http.get(`${environment.API_URL}/xmkt/v1/categories?subcategory=false`).pipe(retry(3))
    }

    fetchSubCategories(): Observable<any> {
      return this.http.get(`${environment.API_URL}/xmkt/v1/categories?subcategory=true`).pipe(retry(3))
    }

    fetchSubCategoryById(id: string): Observable<any> {
      return this.http.get(`${environment.API_URL}/xmkt/v1/categories/subcategory/${id}`).pipe(retry(3))
    }

    createProduct(payload: any): Observable<any> {
      return this.http.post(`${environment.API_URL}/xmkt/v1/products`, payload).pipe(retry(3))
    }

    createBranch(payload: any): Observable<any> {
      return this.http.post(`${environment.API_URL}/xmkt/v1/stores`, payload).pipe(retry(3))
    }

    updateBranch(payload: any): Observable<any> {
      return this.http.put(`${environment.API_URL}/xmkt/v1/stores`, payload).pipe(retry(3))
    }

    updateProduct(payload: any): Observable<any> {
      return this.http.put(`${environment.API_URL}/xmkt/v1/products`, payload).pipe(retry(3))
    }

    deleteProduct(id: any): Observable<any> {
      return this.http.delete(`${environment.API_URL}/xmkt/v1/products/${id}`).pipe(retry(3))
    }

    deleteVariation(payload: any): Observable<any> {
      return this.http.put(`${environment.API_URL}/xmkt/v1/products/variation`, payload).pipe(retry(3))
    }

    fetchProducts(page: number, limit: number, search?: string, status?: string): Observable<any> {
      let url = `${environment.API_URL}/xmkt/v1/products?page=${page}&limit=${limit}`;
      if(search) {
        url = url + `&search=${search}`;
      }
      if(status) {
        url = url + `&status=${status}`;
      }
      return this.http.get(url).pipe(retry(3));
    }

    fetchOrders(page: number, limit: number, search?: string, status?: string): Observable<any> {
      let url = `${environment.API_URL}/xmkt/v1/orders/merchant?page=${page}&limit=${limit}`;
      if(search) {
        url = url + `&search=${search}`;
      }
      if(status) {
        url = url + `&status=${status}`;
      }
      return this.http.get(url).pipe(retry(3));
    }

    fetchBranches(page: number, limit: number, search?: string): Observable<any> {
      let url = `${environment.API_URL}/xmkt/v1/stores?page=${page}&limit=${limit}`;
      if(search) {
        url = url + `&search=${search}`;
      }
      return this.http.get(url).pipe(retry(3));
    }

    deleteBranch(id: any): Observable<any> {
      let url = `${environment.API_URL}/xmkt/v1/stores/${id}`;
      return this.http.delete(url).pipe(retry(3));
    }

    fetchBranch(id: any): Observable<any> {
      let url = `${environment.API_URL}/xmkt/v1/stores/${id}`;
      return this.http.get(url).pipe(retry(3));
    }

    fetchProduct(id: any): Observable<any> {
      return this.http.get(`${environment.API_URL}/xmkt/v1/products/${id}`).pipe(retry(3))
    }

    fetchOrderById(id: any): Observable<any> {
      return this.http.get(`${environment.API_URL}/xmkt/v1/orders/merchant/${id}`).pipe(retry(3))
    }

    updateOrderByStatus(id: any, status: string): Observable<any> {
      return this.http.put(`${environment.API_URL}/xmkt/v1/orders/user/delivery/${id}/${status}`, '').pipe(retry(3));
    }

    fetchProductStats(): Observable<any> {
      return this.http.get(`${environment.API_URL}/xmkt/v1/analytics/products`).pipe(retry(3))
    }

    fetchOrderStats(): Observable<any> {
      return this.http.get(`${environment.API_URL}/xmkt/v1/analytics/orders`).pipe(retry(3))
    }

    fetchDashboardStats(): Observable<any> {
      return this.http.get(`${environment.API_URL}/xmkt/v1/analytics/dashboard`).pipe(retry(3))
    }

    fetchRecentOrders(): Observable<any> {
      return this.http.get(`${environment.API_URL}/xmkt/v1/orders/merchant?page=1&limit=10`).pipe(retry(3))
    }

}